import type { FeedbackType } from '@/modules/perseo-billing/components';

import type { IProviderProductItem } from '../../../shared';

import type { IRsaResponse } from './rsa.interfaces';
import type { SavedCardReturnData } from './saved-cards.interface';
import type { IClubParis } from './summary.interface';

export const ProrratedAmountTypePARIS = 'PARIS';
export const ProrratedAmountTypeMKP = 'MKP';

export const SimulationTypeDebit = 'Debit';
export const SimulationTypeCredit = 'Credit';
export const SimulationTypePrepaid = 'Prepaid';
export const SimulationTypeSVA = 'SVA';
export const SimulationTypeCAT = 'CAT';

export interface PaymentMethod {
  methodId: string;
  sfccMethodId: string;
  enabled: boolean;
  name: string;
  title: string;
  subTitle?: string;
  processorId: string;
  canPayByInvoice?: boolean;
  canPayWithGiftCard?: boolean;
  canPayWithCencopay?: boolean;
  canPayMkpEasyProducts?: boolean;
  attributes: Attribute[];
  customerGroup: { [key: string]: string }[];
}

interface Attribute {
  label: Label;
  value: string;
}

enum Label {
  IsBicEnabled = 'isBicEnabled',
  IsSupportedBankListRequired = 'isSupportedBankListRequired',
  SOSPaymentMethodName = 'SOSPaymentMethodName'
}

export interface PaymentMethodResponseData {
  cencosudCardBINs?: string[];
  isSaga?: boolean;
  safeData?: IRsaResponse;
  paymentsInstruments: PaymentsInstrument[];
  availablePaymentsInstruments?: PaymentMethod[];
  savedCards?: SavedCardReturnData[];
  cencoFees: CencoFee[];
  creditFees: CreditFee[];
  clubParis?: IClubParis;
}
export interface CencoFee {
  fee: number;
}

export interface CreditFee {
  fee: number;
}

export interface PaymentsInstrument {
  id: string;
  title: string;
  subTitle?: string;
  isInvoiceCompatible?: boolean;
  diffPrice?: number | null;
  saveLabel?: string | null;
  totalPrices?: TotalPrice[] | null;
  enable?: boolean;
}

export interface TotalPrice {
  id: string;
  label: string;
  price: number;
  formattedPrice: string;
}

export interface ICardNumberForm {
  cardNumber: string;
}

export interface ICardGiftCardForm {
  cardNumber: string;
  cardCvv: string;
}

export interface ICardRemoveGiftCard {
  paymentInstrumentId: string;
}

export interface IPaymentError {
  statusCode: number;
  error: string;
  title: string;
  message: string;
  code: number;
}

interface PaymentInfoSavedCard {
  paymentInstrumentId: string;
  cvv: string;
  installments: string;
  idKey: string;
}

export interface PaymentInfoCreditCard {
  cardNumber: string;
  cardExpirationDate: string;
  cvv: string;
  installments: number;
  savedCard: boolean;
  holder: string;
  idKey: string;
}

interface PaymentDetails {
  id: string;
}

export interface IPaymentMethodRequest {
  paymentDetails: PaymentDetails;
  paymentInfo?:
    | PaymentInfoCreditCard
    | ICardGiftCardForm
    | PaymentInfoSavedCard;
}

export type CurrencyType = 'CLP' | 'USD';
export type SimulationType =
  | typeof SimulationTypeDebit
  | typeof SimulationTypeCredit
  | typeof SimulationTypePrepaid
  | typeof SimulationTypeSVA
  | typeof SimulationTypeCAT;

export type ProrratedAmountType =
  | typeof ProrratedAmountTypePARIS
  | typeof ProrratedAmountTypeMKP;

export const enum PaymentProcessor {
  GIFT_CARD = 'GIFT_CARD',
  TRANSBANK = 'EPAY_TCOM',
  TRANSBANK_CENCOSUD_CARD = 'EPAY_CENCOSUD_TCOM',
  CENCOPAY = 'CENCOPAY',
  WEBPAY = 'WEBPAY',
  PINPAD = 'PINPAD'
}

export type ProrratedAmount = {
  amount: number;
  type: ProrratedAmountType;
};

export type TComData = {
  inscriptionToken: string;
  cvv: string;
  isNewCard: boolean;
  isSavedCard: boolean;
  installments: number;
};

export type GiftCardDataDetail = {
  amount: number;
  depto: string;
};

export type GiftCardData = {
  token: string;
  details: GiftCardDataDetail[];
};

export type CencopayData = {
  amount: number;
  simulationType: SimulationType;
  paymentType: ProrratedAmount[];
};

export interface ISinglePayment {
  paymentProcessor: PaymentProcessor;
  paymentToken?: string;
  encryptedCVV?: string;
  isNewCard?: boolean;
  saveCard?: boolean;
  installments?: number;
  token?: string;
}

export interface IPaymentRequest {
  payments?: ISinglePayment[];
  isSavedCard?: boolean;
}

export const enum CencopayMethod {
  CENCOPAY_CAT = 'CENCOPAY_CAT',
  CENCOPAY_DEBIT = 'CENCOPAY_DEBIT',
  CENCOPAY_CREDIT = 'CENCOPAY_CREDIT',
  CENCOPAY_SALDO = 'CENCOPAY_SALDO',
  CENCOPAY_PREPAID = 'CENCOPAY_PREPAID'
}

export const enum CencopayTransactionStatus {
  CONFIRMED = 'Confirmed',
  REJECTED = 'Rejected',
  READ_BY_WALLET = 'ReadByWallet'
}

export interface IConfirmOrderIntentBody {
  paymentProcessor: PaymentProcessor;
  orderNumber: string;
  paymentMethodName?: string;
  paymentMethodProcessor?: string;
  token?: string;
  transactionId?: string;
  methodId?: CencopayMethod;
  paymentMethodId?: string;
  clientId?: string;
  email?: string;
  timestamp?: string;
  transactionStatus?: CencopayTransactionStatus;
  errorCode?: string;
  transactionSubType?: string;
  transactionType?: string;
  authorizationInfo?: unknown;
}

export interface IFeesSelectOptions {
  name: string;
  value: number;
}

export interface IExtendedPaymentMethodResponseData {
  isSaga?: boolean;
  safeData?: IRsaResponse;
  paymentsInstruments: IExtendedPaymentsInstrument[];
  savedCards?: SavedCardReturnData[];
  cencoFees: CencoFee[];
  creditFees: CreditFee[];
  clubParis?: IClubParis;
  cencosudCardBINs?: string[];
  reachMaxRetries?: number;
  blockingTime?: number;
}

export enum EComponentType {
  SAVED_CARD = 'SAVED_CARD',
  DIGITAL_WALLET = 'DIGITAL_WALLET',
  OTHER_PAYMENT_METHOD = 'OTHER_PAYMENT_METHOD',
  CLUB = 'CLUB'
}

export interface IExtendedPaymentsInstrument {
  title: string;
  paymentMethods: IExtendedPaymentMethod[];
  componentType: EComponentType;
}

export type TPaymentMethodTotalPrices = Array<{
  id: string;
  label: string;
  formattedPrice: string;
}>;

export interface IExtendedPaymentMethod {
  methodId: string;
  enabled: boolean;
  name: string;
  title: string;
  subTitle?: string;
  processorId: string;
  canPayByInvoice?: boolean;
  attributes: Attribute[];
  customerGroup: { [key: string]: string }[];
  applied?: boolean;
  id?: string;
  isInvoiceCompatible: boolean;
  isCencopayCompatible: boolean;
  isGiftCardCompatible: boolean;
  disableText?: string;
  diffPrice: number | null;
  saveLabel: string | null;
  componentType: EComponentType;
  paymentMethodId?: string;
  paymentInstrumentId?: string;
  cardType?: string | null;
  numberLastDigits?: string;
  isExpired?: boolean;
  bankName?: string;
  isCencosudCard?: boolean;
  isCreditCard?: boolean;
  selected?: boolean;
  sortView?: number;
  details?: null;
  feedBack?: IFeedBack[];
  tooltip?: {
    title: string;
    description: string;
  };
  totalPrices?: TPaymentMethodTotalPrices;
}

export interface IFeedBack {
  title: string;
  subtitle: string;
  type: keyof typeof FeedbackType;
}

export type TErrorInfo = {
  id?: string;
  originErrorCode?: string;
  innerCustomCode?: string;
  message?: string;
  actionType?: {
    type?: string;
    body?: {
      contentAssets?: string;
      title?: string;
      message?: string;
      productDetails?: IProviderProductItem[];
      time?: number;
      url?: string;
    };
  };
};

export type TErrorResponse = {
  status?: number;
  statusCode?: number;
  timestamp?: string;
  message?: string;
  error?: TErrorInfo;
  path?: string;
} | null;
